<template>
  <div>
    <b-card class="rounded-lg">
      <b-card-header>
        <h4>{{ $t('accounting_transactions_create_transfer') || 'Create Transfer Transaction' }}</h4>
      </b-card-header>
      <b-card-body>
        <validation-observer ref="form">
          <b-form @submit.prevent="onSubmit">
            <b-row>
              <b-col
                cols="12"
                md="6"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Cash Out (From Account)"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('accounting_transactions_cash_out') || 'Cash Out (From Account)'"
                    label-for="from_source_id"
                  >
                    <v-select
                      id="from_source_id"
                      v-model="form.from_source_id"
                      :dir="$store.state.appConfig.layout.isRTL ? 'rtl' : 'ltr'"
                      :options="accounts"
                      :reduce="val => val.id"
                      :get-option-label="option => `${option.code} - ${option.name}`"
                      :placeholder="$t('accounting_transactions_cash_out') || 'Select Cash Out Account'"
                      :state="errors.length > 0 ? false : null"
                      @input="onFromAccountChange"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>
              <b-col
                cols="12"
                md="6"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Cash In (To Account)"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('accounting_transactions_cash_in') || 'Cash In (To Account)'"
                    label-for="to_source_id"
                  >
                    <v-select
                      id="to_source_id"
                      v-model="form.to_source_id"
                      :dir="$store.state.appConfig.layout.isRTL ? 'rtl' : 'ltr'"
                      :options="filteredToAccounts"
                      :reduce="val => val.id"
                      :get-option-label="option => `${option.code} - ${option.name}`"
                      :placeholder="$t('accounting_transactions_cash_in') || 'Select Cash In Account'"
                      :state="errors.length > 0 ? false : null"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>
              <b-col
                cols="12"
                md="6"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Amount"
                  rules="required|min_value:0.01"
                >
                  <b-form-group
                    :label="$t('accounting_transactions_amount')"
                    label-for="amount"
                  >
                    <b-form-input
                      id="amount"
                      v-model.number="form.amount"
                      type="number"
                      step="0.01"
                      min="0.01"
                      :placeholder="$t('accounting_transactions_amount')"
                      :state="errors.length > 0 ? false : null"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>
              <b-col cols="12">
                <b-form-group
                  :label="$t('accounting_transactions_description')"
                  label-for="description"
                >
                  <b-form-textarea
                    id="description"
                    v-model="form.description"
                    rows="3"
                    :placeholder="$t('accounting_transactions_description')"
                  />
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col
                cols="12"
                class="mt-2"
              >
                <b-button
                  type="submit"
                  variant="primary"
                  class="mr-1"
                  :disabled="submitting"
                >
                  {{ submitting ? $t('accounting_transactions_saving') : $t('accounting_transactions_save') }}
                </b-button>
                <b-button
                  variant="secondary"
                  @click="$router.go(-1)"
                >
                  {{ $t('accounting_transactions_cancel_btn') }}
                </b-button>
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BButton,
  BRow,
  BCol,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required, min_value } from '@validations'
import vSelect from 'vue-select'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import * as accountsAPI from '@/api/accounting/accounts'

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BButton,
    BRow,
    BCol,
    ValidationProvider,
    ValidationObserver,
    vSelect,
    ToastificationContent,
  },
  data() {
    return {
      submitting: false,
      accounts: [],
      required,
      min_value,
      form: {
        from_source_type: 'account',
        from_source_id: null,
        to_source_type: 'account',
        to_source_id: null,
        amount: null,
        description: null,
      },
    }
  },
  computed: {
    filteredToAccounts() {
      // Filter out the selected "from" account from the "to" account options
      if (this.form.from_source_id && this.accounts) {
        return this.accounts.filter(account => account.id !== this.form.from_source_id)
      }
      return this.accounts
    },
  },
  async mounted() {
    await this.loadAccounts()
  },
  methods: {
    async onSubmit() {
      const isValid = await this.$refs.form.validate()
      if (!isValid) return

      // Validate that from and to accounts are different
      if (this.form.from_source_id === this.form.to_source_id) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('accounting_transactions_messages.same_account_error') || 'Cannot transfer to the same account',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
        return
      }

      this.submitting = true
      try {
        await this.$store.dispatch('accounting/transfers/create', {
          from_source_type: this.form.from_source_type,
          from_source_id: this.form.from_source_id,
          to_source_type: this.form.to_source_type,
          to_source_id: this.form.to_source_id,
          amount: this.form.amount,
          description: this.form.description || null,
        })
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('accounting_transactions_messages.created_success'),
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        this.$router.push({ name: 'accounting-transactions-list' })
      } catch (error) {
        const errorMessage = error.response?.data?.message
                            || (error.response?.data?.errors
                              ? Object.values(error.response.data.errors).flat().join(', ')
                              : this.$t('accounting_transactions_messages.error'))
        this.$toast({
          component: ToastificationContent,
          props: {
            title: errorMessage,
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.submitting = false
      }
    },
    onFromAccountChange() {
      // Reset "to" account if it's the same as "from" account
      if (this.form.from_source_id === this.form.to_source_id) {
        this.form.to_source_id = null
      }
    },
    async loadAccounts() {
      try {
        const response = await accountsAPI.getAccounts({ is_active: true, per_page: 1000 })
        this.accounts = response.data || []
      } catch (error) {
        console.error('Error loading accounts:', error)
      }
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
